import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/** Themengerechte Kurzhilfe für die ganze Oberfläche.
 *
 *  Bewusst *eine* Schicht am Dokument statt einer Komponente um jeden Knopf:
 *  jedes Element mit `title` oder `data-tip` bekommt die Blase, ohne dass es
 *  davon wissen muss. Neue Knöpfe erben das Verhalten damit automatisch.
 *
 *  Der `title` wird beim ersten Überfahren nach `data-tip` umgezogen und vom
 *  Element entfernt — sonst stünden die graue Systemblase und unsere
 *  nebeneinander. Der Text bleibt erhalten, nur die Darstellung wechselt. */

const VERZOEGERUNG = 350;   // ms, bis die Blase kommt
const ABSTAND = 8;          // px zwischen Element und Blase
const RAND = 8;             // px Mindestabstand zum Fensterrand

type Blase = { text: string; x: number; y: number; oben: boolean };

function textVon(el: HTMLElement): string {
  // Erst `title` prüfen, nicht `data-tip`: React setzt ein `title` wieder,
  // sobald sich sein Text ändert („Show the tree" ⇄ „Hide the tree"). Wer
  // hier zuerst auf `data-tip` schaut, lässt das neue `title` stehen — und
  // dann zeigt der Browser seine graue Blase zusätzlich zu unserer.
  const t = el.getAttribute("title");
  if (!t) return el.getAttribute("data-tip") ?? "";
  // Umziehen statt kopieren: der Browser zeigt sonst zusätzlich seine eigene.
  el.setAttribute("data-tip", t);
  el.removeAttribute("title");
  // Ein Knopf, der nur ein Symbol trägt, verliert mit dem `title` seinen
  // Namen für Screenreader — den geben wir zurück.
  if (!el.getAttribute("aria-label") && !el.textContent?.trim()) {
    el.setAttribute("aria-label", t);
  }
  return t;
}

export function TooltipLayer() {
  const [blase, setBlase] = useState<Blase | null>(null);
  const timer = useRef<number | null>(null);
  const ziel = useRef<HTMLElement | null>(null);
  const blaseRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const abbrechen = () => {
      if (timer.current != null) { window.clearTimeout(timer.current); timer.current = null; }
    };
    const verstecken = () => { abbrechen(); ziel.current = null; setBlase(null); };

    const zeigen = (el: HTMLElement, text: string) => {
      const r = el.getBoundingClientRect();
      // Erst unter dem Element versuchen; passt es dort nicht, darüber.
      const platzUnten = window.innerHeight - r.bottom;
      const oben = platzUnten < 56 && r.top > 56;
      setBlase({
        text,
        x: Math.round(r.left + r.width / 2),
        y: Math.round(oben ? r.top - ABSTAND : r.bottom + ABSTAND),
        oben,
      });
    };

    const ueber = (e: Event) => {
      const start = e.target as HTMLElement | null;
      if (!start || typeof start.closest !== "function") return;
      const el = start.closest<HTMLElement>("[data-tip], [title]");
      if (!el) { if (ziel.current) verstecken(); return; }
      if (el === ziel.current) return;
      const text = textVon(el);
      if (!text) { verstecken(); return; }
      abbrechen();
      ziel.current = el;
      setBlase(null);
      timer.current = window.setTimeout(() => zeigen(el, text), VERZOEGERUNG);
    };

    const raus = (e: Event) => {
      const el = ziel.current;
      if (!el) return;
      const nach = (e as MouseEvent).relatedTarget as Node | null;
      if (nach && el.contains(nach)) return;
      verstecken();
    };

    const taste = (e: KeyboardEvent) => { if (e.key === "Escape") verstecken(); };

    document.addEventListener("pointerover", ueber, true);
    document.addEventListener("pointerout", raus, true);
    document.addEventListener("focusin", ueber, true);
    document.addEventListener("focusout", verstecken, true);
    // Beim Klicken, Scrollen und Verkleinern verliert die Position ihren Sinn.
    document.addEventListener("pointerdown", verstecken, true);
    window.addEventListener("scroll", verstecken, true);
    window.addEventListener("resize", verstecken);
    window.addEventListener("keydown", taste);
    return () => {
      abbrechen();
      document.removeEventListener("pointerover", ueber, true);
      document.removeEventListener("pointerout", raus, true);
      document.removeEventListener("focusin", ueber, true);
      document.removeEventListener("focusout", verstecken, true);
      document.removeEventListener("pointerdown", verstecken, true);
      window.removeEventListener("scroll", verstecken, true);
      window.removeEventListener("resize", verstecken);
      window.removeEventListener("keydown", taste);
    };
  }, []);

  // Nach dem Messen waagerecht in den sichtbaren Bereich schieben, damit die
  // Blase am Fensterrand nicht abgeschnitten wird.
  useEffect(() => {
    const el = blaseRef.current;
    if (!el || !blase) return;
    const b = el.getBoundingClientRect();
    let dx = 0;
    if (b.left < RAND) dx = RAND - b.left;
    else if (b.right > window.innerWidth - RAND) dx = window.innerWidth - RAND - b.right;
    el.style.transform = `translate(calc(-50% + ${dx}px), ${blase.oben ? "-100%" : "0"})`;
  }, [blase]);

  if (!blase) return null;
  return createPortal(
    <div
      ref={blaseRef}
      role="tooltip"
      className={`ui-tip${blase.oben ? " is-oben" : ""}`}
      style={{
        left: blase.x,
        top: blase.y,
        transform: `translate(-50%, ${blase.oben ? "-100%" : "0"})`,
      }}
    >
      {blase.text}
    </div>,
    document.body,
  );
}
